import React from 'react';
import { RotateCcwIcon, TableIcon } from 'lucide-react';
import { Card, CardHeader } from './Card';
import { TierMinPanel } from './TierMinPanel';
import type { EstimatorColumn, TierPlan, WhatIfInputs } from '../types/commission';
import { formatBps, formatCurrency, formatPercent, formatPrice } from '../utils/format';
interface PayEstimatorProps {
  plan: TierPlan;
  columns: EstimatorColumn[];
  whatIf: WhatIfInputs;
  onWhatIfChange: (next: WhatIfInputs) => void;
  onReset: () => void;
  monthLabel: string;
  daysRemaining: number;
}
interface Row {
  label: string;
  render: (column: EstimatorColumn) => string;
  emphasis?: boolean;
  muted?: boolean;
}
const rows: Row[] = [{
  label: 'Loan goal',
  render: c => `${c.goalUnits}`,
  muted: true
}, {
  label: 'Total loans',
  render: c => c.units.toFixed(c.key === 'actuals' ? 0 : 1)
}, {
  label: 'Loans to goal %',
  render: c => formatPercent(c.goalAttainment)
}, {
  label: 'Funded volume',
  render: c => formatCurrency(c.volume)
}, {
  label: 'Base amount (loan pay)',
  render: c => formatCurrency(c.basePay)
}, {
  label: 'Tier bonus',
  render: c => formatCurrency(c.tierBonus)
}, {
  label: 'Overages',
  render: c => formatCurrency(c.overageShare)
}, {
  label: 'Loans to next tier',
  render: c => c.nextTier ? `${Math.ceil(c.unitsToNextTier)} → ${c.nextTier.name}` : 'Top tier',
  muted: true
}, {
  label: 'Total payout',
  render: c => formatCurrency(c.totalPayout),
  emphasis: true
}];
const columnStyles: Record<string, string> = {
  actuals: 'bg-white',
  pacing: 'bg-slate-50',
  whatIf: 'bg-brand-50/60'
};
export function PayEstimator({
  plan,
  columns,
  whatIf,
  onWhatIfChange,
  onReset,
  monthLabel,
  daysRemaining
}: PayEstimatorProps) {
  const [actuals, pacing] = columns;
  return <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_280px]" data-id="element-105" data-mp="2j7zysx9b8srz">
      <Card data-id="element-106" data-mp="2javwckh8xffw">
        <CardHeader title="Incentive-based pay estimator" description={`${monthLabel} · ${daysRemaining} business days left · pacing includes weighted active pipeline`} icon={<TableIcon className="h-4 w-4" aria-hidden="true" data-id="element-108" data-mp="p0o17iwx4tef" />} data-id="element-107" data-mp="2o7cv08m2nc78" />
        <div className="overflow-x-auto" data-id="element-109" data-mp="oykzw2qbfdue">
          <table className="w-full min-w-[720px] border-collapse text-sm" data-id="element-110" data-mp="k13298hr0l1p">
            <caption className="sr-only" data-id="element-111" data-mp="fzzlmjrvmkzf">
              Commission actuals, current pacing, and what-if scenario
            </caption>
            <thead data-id="element-112" data-mp="1cqicvx5i4bx3">
              <tr className="border-b border-slate-200" data-id="element-113" data-mp="1coyrxt8whcoz">
                <th scope="col" className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-114" data-mp="17rf88dtfo7pn">
                  Measure
                </th>
                {columns.map(column => <th key={column.key} scope="col" className={`px-5 py-3 text-right text-sm font-semibold text-slate-900 ${columnStyles[column.key]}`} data-id="element-115" data-mp="yra88oqkkbli">
                    {column.label}
                    <span className="mt-0.5 block text-xs font-medium text-slate-500" data-id="element-116" data-mp="1yzqk1s1nhk13">
                      {column.tier.name} · {formatBps(column.tier.basisPoints)}
                    </span>
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-117" data-mp="2cqlsx4c5g14o">
              {rows.map(row => <tr key={row.label} className="border-b border-slate-100 last:border-0" data-id="element-118" data-mp="2cs6o8w6afo3l">
                  <th scope="row" className={`px-5 py-3 text-left font-medium ${row.emphasis ? 'text-slate-900' : row.muted ? 'text-slate-500' : 'text-slate-700'}`} data-id="element-119" data-mp="2hox2rf2g2bmw">
                    {row.label}
                  </th>
                  {columns.map(column => <td key={column.key} className={`px-5 py-3 text-right tabular-nums ${columnStyles[column.key]} ${row.emphasis ? 'text-base font-semibold text-slate-900' : row.muted ? 'text-slate-500' : 'text-slate-800'} ${row.emphasis && column.key === 'whatIf' ? 'text-brand-800' : ''}`} data-id="element-120" data-mp="xadarexvu8kd">
                      {row.render(column)}
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>
        <p className="border-t border-slate-100 bg-slate-50 px-5 py-3 text-xs text-slate-500" data-id="element-121" data-mp="3ehbn2x00akrd">
          Estimates only. This calculator is meant to provide early estimates of pay incentive pay and
          does not represent money owed or promised — final commission is confirmed at payroll.
        </p>
      </Card>

      <div className="space-y-5" data-id="element-122" data-mp="30nueg78uysg4">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-123" data-mp="30n2ll46nb8tj">
          <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3" data-id="element-124" data-mp="2vpskgr9w98pv">
            <div data-id="element-125" data-mp="1b6p52rn2gbka">
              <h3 className="text-sm font-semibold text-slate-900" data-id="element-126" data-mp="1b66wqwn4q6w0">What if</h3>
              <p className="mt-0.5 text-xs text-slate-500" data-id="element-127" data-mp="1g01h82ak6pw5">Add deals on top of the pacing column.</p>
            </div>
            <button type="button" onClick={onReset} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-128" data-mp="125ilg9p4ekmt">
              <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-129" data-mp="1ky38vbm1vxj8" />
              Reset
            </button>
          </div>
          <div className="space-y-4 px-4 py-4" data-id="element-130" data-mp="1pvv166qlxlny">
            <label className="flex items-start gap-2.5 text-xs text-slate-600" data-id="element-131" data-mp="1pyqyptyjm8bv">
              <input type="checkbox" checked={whatIf.includePipeline} onChange={event => onWhatIfChange({
              ...whatIf,
              includePipeline: event.target.checked
            })} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" data-id="element-132" data-mp="9hczjjmbyple" />
              <span data-id="element-133" data-mp="26g24fpfu8bhv">
                Build the what-if on top of current pacing ({pacing.units.toFixed(1)} loans).
                Unchecked starts from actuals ({actuals.units}).
              </span>
            </label>

            <div data-id="element-134" data-mp="1xi09nrmp6f31">
              <div className="flex items-baseline justify-between" data-id="element-135" data-mp="1xecd0jlyhck3">
                <label htmlFor="what-if-units" className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-136" data-mp="1sgkntxcp44jl">
                  Extra loans
                </label>
                <span className="text-sm font-semibold tabular-nums text-slate-900" data-id="element-137" data-mp="1jiqt2qukxiyj">
                  +{whatIf.extraUnits}
                </span>
              </div>
              <input id="what-if-units" type="range" min={0} max={8} step={1} value={whatIf.extraUnits} onChange={event => onWhatIfChange({
              ...whatIf,
              extraUnits: Number(event.target.value)
            })} className="mt-2 w-full accent-brand-600" data-id="element-138" data-mp="3dvqberqcgr9i" />
            </div>

            <div data-id="element-139" data-mp="1dgf44og5ykdo">
              <div className="flex items-baseline justify-between" data-id="element-140" data-mp="go1ti16x19e1">
                <label htmlFor="what-if-size" className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-141" data-mp="gpwf7hq0h5j6">
                  Avg loan amount
                </label>
                <span className="text-sm font-semibold tabular-nums text-slate-900" data-id="element-142" data-mp="untewmzrhs22">
                  {formatCurrency(whatIf.averageLoanAmount)}
                </span>
              </div>
              <input id="what-if-size" type="range" min={120_000} max={600_000} step={10_000} value={whatIf.averageLoanAmount} onChange={event => onWhatIfChange({
              ...whatIf,
              averageLoanAmount: Number(event.target.value)
            })} className="mt-2 w-full accent-brand-600" data-id="element-143" data-mp="2169ff1ku40vt" />
            </div>

            <div data-id="element-144" data-mp="1lr84bwkg0wbc">
              <div className="flex items-baseline justify-between" data-id="element-145" data-mp="1lqq5cs6ec3zq">
                <label htmlFor="what-if-price" className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-146" data-mp="20fuocbl0mc8w">
                  Selling for
                </label>
                <span className="text-sm font-semibold tabular-nums text-slate-900" data-id="element-147" data-mp="29gsv31mu8xv6">
                  {formatPrice(whatIf.sellPrice)}
                </span>
              </div>
              <input id="what-if-price" type="range" min={99.5} max={102.5} step={0.125} value={whatIf.sellPrice} onChange={event => onWhatIfChange({
              ...whatIf,
              sellPrice: Number(event.target.value)
            })} className="mt-2 w-full accent-brand-600" data-id="element-148" data-mp="f4cjrmf6qtjg" />
            </div>
          </div>
        </div>

        <TierMinPanel plan={plan} actuals={actuals} pacing={pacing} data-id="element-149" data-mp="3sc6yw7ziwinh" />
      </div>
    </div>;
}